import type { UniverseId } from '@/domain/universes/universe.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import type { DeliveryPointId } from '@/interfaces/DeliveryPoint.interface';
import type { LiteralValues } from '@/utils/typing/literal-values.type';
import type {
    MipActionKind,
    MipStandardAction,
    MipTheoreticalBenefitPreference,
    MipTheoreticalCostPreference
} from '../mip-standard-action/mip-standard-action.interface';
import type { MipActionId, MipInvestmentPlanId, MipScenarioId, MipStandardActionId } from '../shared/mip-ids.interface';

export type { MipScenarioId } from '../shared/mip-ids.interface';

export interface MipScenariosGeneralInfo {
    name: string;
    description?: string;
}

export const MipScenarioCreationStep = {
    INFORMATION_GENERAL: 'INFORMATION_GENERAL',
    ADD_STANDARD_ACTIONS: 'ADD_STANDARD_ACTIONS'
} as const;

export type MipScenarioCreationStep = LiteralValues<typeof MipScenarioCreationStep>;

export enum MipScenarioStepNumber {
    INFORMATION_GENERAL = 1,
    ADD_STANDARD_ACTIONS = 2
}

export interface MipStepStates {
    [step: string]: { active: boolean; completed: boolean };
}

export interface MipInvestmentPlanScenarioCreationDto {
    planId: MipInvestmentPlanId;
    name: string;
    description?: string;
}

export interface MipInvestmentPlanScenarioEditionDto {
    planId: MipInvestmentPlanId;
    scenarioId: MipScenarioId;
    name: string;
    description?: string | null;
}

export interface MipScenarioDto {
    planId: MipInvestmentPlanId;
    scenarioId: MipScenarioId;
    name: string;
    description?: string | null;
    buildingCount?: number | null;
    dpCount?: number | null;
}

export interface MipScenarioKpiDto {
    scenarioId: MipScenarioId;
    scenarioName: string;
    surface?: number | null;
    actionCount?: number | null;
    energySaving?: number | null;
    co2Saving?: number | null;
    costVatInclSaving?: number | null;
    costVatExclSaving?: number | null;
    capex?: number | null;
    roi?: number | null;
}

export interface MipActionSummary {
    mipActionId: MipActionId;
    buildingCount: number;
    standardAction: MipStandardAction;
}

export interface MipActionDetail {
    buildingId: BuildingId;
    deliveryPointsIds: DeliveryPointId[];

    theoreticalEnergyBenefit: number | null;
    theoreticalBenefitPreference: MipTheoreticalBenefitPreference | null;
    theoreticalCost: number | null;
    theoreticalCostPreference: MipTheoreticalCostPreference | null;
    universeCode?: UniverseId | null;
    startYear: Temporal.PlainDate;
}

export interface MipActionCreationResponse {
    mipActionId: MipActionId;
    scenarioId: MipScenarioId;
    standardActionId: MipStandardActionId;
}

export interface MipScenarioActionSummaryResponse {
    mipActionId: MipActionId;

    standardActionId: MipStandardActionId;
    actionCategory: MipActionKind;
    actionKey: string;
    defaultTheoreticalEnergyBenefit?: number | null;
    defaultTheoreticalBenefitPreference?: MipTheoreticalBenefitPreference | null;
    defaultTheoreticalCost?: number | null;
    defaultTheoreticalCostPreference?: MipTheoreticalCostPreference | null;

    buildingNumber: number;
}

export interface MipValueEvolutionDto {
    year: number;
    universeCode: UniverseId;
    gain: number;
    cumulatedGain: number;
}

export interface MipBudgetEvolutionDto {
    year: number;
    universeCode: UniverseId;
    budget: number;
    cumulatedBudget: number;
    capex: number;
    cumulatedTreasury: number;
}

export interface MipEnergyByActionKeyDto {
    actionKey: string;
    universeCode: UniverseId;
    energySavings: number;
}

export interface MipScenarioSynthesisDto {
    energyEvolution: MipValueEvolutionDto[];
    co2Evolution: MipValueEvolutionDto[];
    budgetEvolution: MipBudgetEvolutionDto[];
    energyByActionKey: MipEnergyByActionKeyDto[];
    referenceConsumption: number;
    finalConsumption: number;
}

export interface MipScenarioActionDetailResponse {
    buildingId: BuildingId;
    buildingName?: string | null;
    floorSurface?: number | null;
    referenceYearEnergyConsumption?: number | null;
    startYear?: number | null;
    theoreticalEnergyBenefit?: number | null;
    theoreticalCost?: number | null;
    dpIds: DeliveryPointId[];
}

export interface MipScenarioActionsTemplateRow {
    status: null;
    actionTitle: string;
    actionKey: string;
    actionCategory: string;
    buildingName: string | null;
    buildingExternalCode: string | null;
    theoreticalBenefitUnit: string;
    theoreticalCostUnit: string;
    startYear: number | null;
    theoreticalEnergyBenefit: number | null;
    theoreticalCost: number | null;
    universes: UniverseId[] | null;
}

export interface ScenarioKpiDisplay {
    title: string;
    value: number | null | undefined;
    valueSubtitle?: string;
    getFormatter: () => (value: number) => string;
}
